/**
 * PlanificarParadaDialog
 * ----------------------
 * Modal para planificar qué componentes van en una parada concreta de una ruta.
 *
 * Casos de uso (Iter 55):
 *  - 3 lonas YSL en Druni Airesur, han llegado solo 2 → marca solo esas 2 en la parada
 *    de hoy. Las otras 1 quedan "sin asignar" para otra fecha/ruta.
 *  - El admin puede mover componentes entre paradas sin tocar la línea.
 */
import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Package, Calendar, Save } from "lucide-react";

export default function PlanificarParadaDialog({
  open, onOpenChange, rutaId, paradaIdx, parada, fechaRuta, onSaved,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [todosComps, setTodosComps] = useState([]);   // componentes de la línea
  const [seleccionados, setSeleccionados] = useState(new Set()); // ids marcados
  const [fechaPlan, setFechaPlan] = useState(fechaRuta || "");

  const cargar = async () => {
    if (!parada?.linea_id) return;
    setLoading(true);
    try {
      const r = await api.get(`/lineas/${parada.linea_id}/componentes`);
      const padres = (r.data || []).filter((c) => !c.parent_id);
      setTodosComps(padres);
      const incluidos = new Set(parada.componentes_incluidos || padres.map((c) => c.id));
      setSeleccionados(incluidos);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando componentes");
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable */
  useEffect(() => {
    if (open && parada?.linea_id) {
      cargar();
      setFechaPlan(fechaRuta || "");
    }
  }, [open, parada?.linea_id, fechaRuta]);
  /* eslint-enable */

  const toggle = (cid) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  const seleccionarTodos = () => setSeleccionados(new Set(todosComps.map((c) => c.id)));
  const limpiar = () => setSeleccionados(new Set());

  const guardar = async () => {
    setSaving(true);
    try {
      const ids = Array.from(seleccionados);
      // 1) Asignar los marcados a esta parada
      if (ids.length > 0) {
        await api.post(`/rutas/${rutaId}/paradas/${paradaIdx}/componentes`, {
          componente_ids: ids,
          fecha_montaje_planificada: fechaPlan || null,
        });
      }
      // 2) Quitar los que ya estaban y han sido desmarcados
      const previos = new Set(parada.componentes_incluidos || []);
      const aQuitar = [...previos].filter((cid) => !seleccionados.has(cid));
      for (const cid of aQuitar) {
        await api.delete(`/rutas/${rutaId}/paradas/${paradaIdx}/componentes/${cid}`);
      }
      toast.success(`Parada actualizada · ${ids.length} componente(s) asignado(s)`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const sinAsignarOtros = useMemo(() => {
    // Componentes seleccionados que estaban en otra parada (visual hint)
    return todosComps.filter((c) =>
      seleccionados.has(c.id) && c.parada_ruta_id && c.parada_ruta_id !== rutaId
    );
  }, [todosComps, seleccionados, rutaId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="planificar-parada-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-400" />
            Planificar componentes en esta parada
          </DialogTitle>
          <DialogDescription>
            Marca qué componentes de la línea van en <b>esta parada concreta</b> (en {parada?.espacio?.nombre || "este PDV"}).
            Los que dejes desmarcados quedarán <b>pendientes</b> para asignar a otra fecha/ruta.
          </DialogDescription>
        </DialogHeader>

        {/* Fecha de montaje planificada */}
        <div className="flex items-center gap-3 bg-zinc-950 border border-zinc-800 rounded-lg p-3">
          <Calendar className="w-4 h-4 text-cyan-400" />
          <div className="flex-1">
            <div className="text-xs text-zinc-400">Fecha de montaje planificada (para los componentes marcados)</div>
            <input
              type="date"
              value={fechaPlan}
              onChange={(e) => setFechaPlan(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-100 mt-1"
              data-testid="fecha-planificada-input"
            />
          </div>
          <div className="text-[11px] text-zinc-500 text-right">
            Por defecto coincide con la fecha de la ruta. Cámbiala si planificas otra fecha distinta.
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-zinc-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando componentes…
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs text-zinc-400">
              <span>
                <b className="text-amber-300">{seleccionados.size}</b> de {todosComps.length} componentes
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={seleccionarTodos} data-testid="select-all-btn">
                  Marcar todos
                </Button>
                <Button size="sm" variant="ghost" onClick={limpiar} data-testid="clear-btn">
                  Desmarcar
                </Button>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto border border-zinc-800 rounded-lg divide-y divide-zinc-800/60">
              {todosComps.length === 0 && (
                <div className="text-center text-zinc-500 py-8 text-sm">
                  Esta línea no tiene componentes físicos.
                </div>
              )}
              {todosComps.map((c) => {
                const checked = seleccionados.has(c.id);
                const enOtraParada = c.parada_ruta_id && c.parada_ruta_id !== rutaId;
                const estadoComp = c.estado || "pendiente";
                return (
                  <label
                    key={c.id}
                    className="flex items-start gap-3 p-3 cursor-pointer hover:bg-zinc-900/50"
                    data-testid={`comp-row-${c.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggle(c.id)}
                      data-testid={`comp-check-${c.id}`}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-zinc-100 truncate">{c.nombre}</span>
                        {c.cantidad > 1 && (
                          <Badge variant="outline" className="text-[10px]">×{c.cantidad}</Badge>
                        )}
                        {c.ancho_cm && c.alto_cm && (
                          <span className="text-[11px] text-zinc-500">{c.ancho_cm}×{c.alto_cm}cm</span>
                        )}
                        {c.bulto_numero && (
                          <Badge className="text-[10px] bg-emerald-500/15 text-emerald-300 border-emerald-500/30" variant="outline">
                            📦 Bulto {c.bulto_numero}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-1">
                        <span>Estado: <span className="text-zinc-300">{estadoComp}</span></span>
                        {c.fecha_entrada && (
                          <span>Entrada: {c.fecha_entrada}</span>
                        )}
                        {enOtraParada && (
                          <Badge variant="outline" className="text-[9px] text-amber-300 border-amber-500/40">
                            ⚠ Estaba en otra parada
                          </Badge>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {sinAsignarOtros.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-200">
                ⚠ Vas a mover <b>{sinAsignarOtros.length}</b> componente(s) desde otras paradas a ésta.
                Esas paradas perderán esos componentes.
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={saving || loading} data-testid="save-planificar-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar planificación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
